import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import ProductEditModal from '../../components/feature/ProductEditModal';
import ProductArrangePanel from '../../components/feature/ProductArrangePanel';
import { Product } from '../../mocks/products';
import { useAuth } from '../../contexts/AuthContext';
import { useCategoryContext, CategoryItem } from '../../contexts/CategoryContext';

function ProductCard({ product }: { product: Product }) {
  const volumeDisplay = product.volumeOptions && product.volumeOptions.length > 1
    ? product.volumeOptions.join(', ')
    : product.volume;

  return (
    <Link to={`/products/${product.id}`} className="group cursor-pointer">
      <div className="w-full aspect-square rounded-lg overflow-hidden mb-3 md:mb-4">
        <img
          src={product.thumbnail}
          alt={product.name}
          className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <p className="text-xs md:text-sm text-emerald-600 mb-1">{product.nameEn}</p>
      <h3 className="text-sm md:text-base font-medium text-gray-800 mb-1 md:mb-2">
        {product.name}
        <span className="text-xs md:text-sm font-normal text-gray-500 ml-1 md:ml-2">{volumeDisplay}</span>
      </h3>
      <p className="text-xs md:text-sm text-gray-500 leading-relaxed line-clamp-2">{product.recommendation}</p>
    </Link>
  );
}

export default function ProductsPage() {
  const { isAdmin } = useAuth();
  const { categoryList, dynamicProducts, addProduct, updateProduct, deleteProduct, reorderCategories, reorderProducts } = useCategoryContext();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isArrangeMode, setIsArrangeMode] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const navigate = useNavigate();

  const handleSaveProduct = (product: Product) => {
    const finalProduct = {
      ...product,
      id: product.id || `product_${Date.now()}`,
    };
    const exists = dynamicProducts.find((p) => p.id === finalProduct.id);
    if (exists) {
      updateProduct(finalProduct);
    } else {
      addProduct(finalProduct);
    }
    setIsEditModalOpen(false);
    setEditingProduct(null);
    navigate(0);
  };

  const handleDeleteProduct = (id: string) => {
    deleteProduct(id);
    setIsEditModalOpen(false);
    setEditingProduct(null);
    navigate(0);
  };

  const handleOpenEdit = (product?: Product | null) => {
    setEditingProduct(product || null);
    setIsEditModalOpen(true);
  };

  const handleArrangeSave = (newCategoryOrder: CategoryItem[], productOrders: Record<string, Product[]>) => {
    // 카테고리 순서 저장
    reorderCategories(newCategoryOrder);
    // 각 카테고리별 제품 순서 저장
    Object.entries(productOrders).forEach(([categoryId, products]) => {
      reorderProducts(categoryId, products);
    });
    setIsArrangeMode(false);
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-0 md:pt-20">
        <section className="py-12 md:py-24 pb-20 md:pb-40 bg-white" data-product-shop>
          <div className="max-w-[1400px] mx-auto px-5">
            {isAdmin && (
              <div className="flex justify-end items-center gap-3 mb-6">
                {isArrangeMode ? (
                  <>
                    <p className="text-sm text-gray-500 mr-2">
                      <i className="ri-information-line mr-1"></i>
                      제품을 드래그해서 순서를 변경하세요. 카테고리 이름을 드래그하면 카테고리 순서가 바뀝니다.
                    </p>
                    <button
                      onClick={() => setIsArrangeMode(false)}
                      className="border border-gray-300 text-gray-600 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-close-line text-lg"></i>
                      취소
                    </button>
                    <button
                      onClick={() => {
                        // ArrangePanel 내부에서 save 트리거 - ref 방식 대신 상태로 처리
                        const saveBtn = document.getElementById('arrange-save-trigger');
                        if (saveBtn) saveBtn.click();
                      }}
                      className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-save-line text-lg"></i>
                      저장
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setIsArrangeMode(true)}
                      className="border border-emerald-600 text-emerald-600 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-layout-grid-line text-lg"></i>
                      상품 진열 수정
                    </button>
                    <button
                      onClick={() => handleOpenEdit(null)}
                      className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-add-line text-lg"></i>
                      상품 등록
                    </button>
                  </>
                )}
              </div>
            )}

            {isArrangeMode ? (
              <ProductArrangePanel
                categoryList={categoryList}
                dynamicProducts={dynamicProducts}
                onSave={handleArrangeSave}
                onCancel={() => setIsArrangeMode(false)}
                saveTriggerId="arrange-save-trigger"
              />
            ) : (
              <>
                {categoryList.map((category, index) => {
                  const categoryProducts = dynamicProducts.filter(
                    (p) => p.category === category.id
                  );

                  // 제품이 없으면 숨김 (기본/커스텀/isDefault 모두 동일하게 적용)
                  if (categoryProducts.length === 0) return null;

                  return (
                    <div key={category.id} id={`category-${category.id}`}>
                      <div className="mb-6 md:mb-10 text-center">
                        <h2 className="text-xl md:text-3xl font-bold text-[#222222] mb-2 md:mb-3">
                          {category.title}
                        </h2>
                        {category.description && (
                          <p className="text-sm md:text-base text-gray-500 leading-relaxed">
                            {category.description}
                          </p>
                        )}
                      </div>

                      {categoryProducts.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 md:gap-x-8 gap-y-8 md:gap-y-12">
                          {categoryProducts.map((product) => (
                            <ProductCard key={product.id} product={product} />
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                          <i className="ri-inbox-line text-4xl mb-3"></i>
                          <p className="text-sm">아직 등록된 제품이 없습니다.</p>
                          {isAdmin && (
                            <button
                              onClick={() => handleOpenEdit(null)}
                              className="mt-4 text-sm text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer flex items-center gap-1 whitespace-nowrap"
                            >
                              <i className="ri-add-line"></i>
                              첫 번째 제품 등록하기
                            </button>
                          )}
                        </div>
                      )}

                      {index < categoryList.length - 1 && (
                        <div className="my-[8vh] md:my-[15vh] border-t border-gray-200"></div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTopButton />

      <ProductEditModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingProduct(null); }}
        product={editingProduct}
        onSave={handleSaveProduct}
        onDelete={handleDeleteProduct}
      />
    </div>
  );
}